/**
 * Чистая логика GPS-трекера: фильтрация, пакеты, офлайн-буфер.
 *
 * Без Capacitor и без сети — покрывается unit-тестами.
 */

import { TRACE } from "@hive/shared/balance";
import { latLngToCell } from "h3-js";

import { GPS_OFFLINE_STORAGE_KEY } from "./constants.js";
import { HEX_RESOLUTION } from "../map/hexes.js";

export type TraceGpsPoint = {
  readonly lat: number;
  readonly lng: number;
  readonly accuracy: number;
  readonly recordedAt: string;
};

export type PendingTraceBatch = {
  readonly idempotencyKey: string;
  readonly points: readonly TraceGpsPoint[];
};

/** Точка проходит клиентский фильтр accuracy (сервер решает качество Следа). */
export function acceptGpsAccuracy(accuracyMeters: number): boolean {
  return accuracyMeters <= TRACE.maxAccuracyMeters;
}

/** H3 res 10 для соты в точке. */
export function hexAtPoint(lat: number, lng: number): string {
  return latLngToCell(lat, lng, HEX_RESOLUTION);
}

/** Нужно ли отправить накопленный буфер: таймер или смена соты. */
export function shouldFlushBatch(input: {
  nowMs: number;
  lastFlushMs: number;
  currentHex: string | null;
  lastFlushedHex: string | null;
}): boolean {
  if (input.currentHex !== null && input.lastFlushedHex !== null && input.currentHex !== input.lastFlushedHex) {
    return true;
  }
  return input.nowMs - input.lastFlushMs >= TRACE.batchIntervalSeconds * 1000;
}

/** Ключ идемпотентности: один пакет — один ключ при повторе после офлайна. */
export function makeBatchIdempotencyKey(firstRecordedAt: string, pointCount: number): string {
  return `trace-batch-${firstRecordedAt}-${pointCount}`;
}

export function readOfflineBatches(storage: Pick<Storage, "getItem">): PendingTraceBatch[] {
  const raw = storage.getItem(GPS_OFFLINE_STORAGE_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isPendingBatch);
  } catch {
    return [];
  }
}

export function writeOfflineBatches(
  storage: Pick<Storage, "setItem" | "removeItem">,
  batches: readonly PendingTraceBatch[],
): void {
  if (batches.length === 0) {
    storage.removeItem(GPS_OFFLINE_STORAGE_KEY);
    return;
  }
  storage.setItem(GPS_OFFLINE_STORAGE_KEY, JSON.stringify(batches));
}

/** Добавить пакет в офлайн-очередь, обрезая до лимита точек суммарно. */
export function enqueueOfflineBatch(
  batches: readonly PendingTraceBatch[],
  batch: PendingTraceBatch,
): PendingTraceBatch[] {
  const merged = [...batches, batch];
  let totalPoints = merged.reduce((sum, item) => sum + item.points.length, 0);
  const trimmed = [...merged];
  while (totalPoints > TRACE.offlineBufferMax && trimmed.length > 0) {
    const removed = trimmed.shift();
    if (removed) totalPoints -= removed.points.length;
  }
  return trimmed;
}

function isPendingBatch(value: unknown): value is PendingTraceBatch {
  if (typeof value !== "object" || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record["idempotencyKey"] === "string" &&
    Array.isArray(record["points"]) &&
    record["points"].every(isTracePoint)
  );
}

function isTracePoint(value: unknown): value is TraceGpsPoint {
  if (typeof value !== "object" || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record["lat"] === "number" &&
    typeof record["lng"] === "number" &&
    typeof record["accuracy"] === "number" &&
    typeof record["recordedAt"] === "string"
  );
}
